import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { socketEventEmitter } from "../socket";
import { botSocketEventEmitter } from "../botSocket";
import {
    Box,
    AppBar,
    Menu,
    MenuItem,
    IconButton,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
} from "@mui/material";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import MenuOpenOutlinedIcon from "@mui/icons-material/MenuOpenOutlined";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import { useAuth } from "../context/AuthContext";
import { useNotification } from "../context/NotificationContext";
import { useProject } from "../context/ProjectContext";
import { useSidebar } from "../context/SidebarContext";
import { useAllProjects } from "../context/AllProjectsContext";
import TaskService from "../services/TaskService";
import ReviewModal from "./modals/ReviewModal";
import SessionExpiredModal from "./modals/SessionExpiredModal";
import EnterOTPModal from "./modals/EnterOTPModal";
import LoginWasNotCompletedModal from "./modals/LoginWasNotCompletedModal";
import TaskLoad from "./TaskLoad";
import ProjectSelect from "./ProjectSelect";
import HomeIcon from "assets/iconComponents/HomeIcon";

const AUTOMATIC_LOGIN_TASK_TYPES = new Set([
    "Login Elements Processing",
    "Login Test",
]);

const Header = ({ projectsToggler = false }) => {
    const { user, logout } = useAuth();
    const showNotification = useNotification();
    const navigate = useNavigate();
    const location = useLocation();
    const { selectedProject, setSelectedProject } = useProject();
    const { collapsedSidebar, setCollapsedSidebar } = useSidebar();
    const { allProjects } = useAllProjects();
    const { id } = useParams();

    const [anchorEl, setAnchorEl] = useState(null);
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [loadStatus, setLoadStatus] = useState(null);

    const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
    const [sessionExpiredPayload, setSessionExpiredPayload] = useState(null);
    const [enterOTPOpen, setEnterOTPOpen] = useState(false);
    const [loginWasNotCompletedOpen, setLoginWasNotCompletedOpen] =
        useState(false);
    // const [unsupportedPageOpen, setUnsupportedPageOpen] = useState(false); // removed
    // const [unsupportedExpectedText, setUnsupportedExpectedText] = useState(""); // removed

    // New: quota exceeded dialog state
    const [quotaExceededOpen, setQuotaExceededOpen] = useState(false);
    const [quotaInfo, setQuotaInfo] = useState(null);

    const open = Boolean(anchorEl);

    // const UNSUPPORTED_PAGE_ERROR = "This type of page isn't supported yet."; // removed

    useEffect(() => {
        const isSelectedProjectExist = allProjects.find(
            (project) => project?.id === selectedProject?.id
        );
        const isProjectSelectionCleared =
            sessionStorage.getItem("projectSelectionCleared") === "true";

        if (selectedProject?.id && allProjects.length > 0 && !isSelectedProjectExist) {
            setSelectedProject(undefined);
        } else if (
            !selectedProject?.id &&
            !isProjectSelectionCleared &&
            allProjects.length === 1
        ) {
            setSelectedProject(allProjects[0]);
        }

        if (id) {
            const initialProject = allProjects.find(
                (project) => project.id === id
            );
            if (initialProject) {
                setSelectedProject(initialProject);
            }
        }

        const handleTaskCompleted = async (data) => {
            if (data.projectId === selectedProject?.id) {
                if (data.type === "UI Scan") {
                    showNotification(
                        `${data.type} has been completed successfully`,
                        "success",
                        "Learn more",
                        navigate,
                        `/pages/${data.pageId}`,
                        null,
                        `Page: ${data.pageTitle}`
                    );
                } else if (
                    data.type === "Login Test" ||
                    data.type === "Login Elements Processing"
                ) {
                    showNotification(
                        `${data.type} has been completed successfully`,
                        "success",
                        "Learn more",
                        navigate,
                        `/project/${data.projectId}`,
                        { state: { activeTab: "2" } }
                    );
                } else if (data.type === "Test Case") {
                    showNotification(
                        `${data.type} has been completed successfully`,
                        "success",
                        "Learn more",
                        navigate,
                        `/test-case/${data.testCaseId}`
                    );
                } else if (data.type === "Journey Test Cases") {
                    showNotification(
                        `${data.type} has been completed successfully`,
                        "success",
                        "Learn more",
                        navigate,
                        "/test-journeys"
                    );
                }
                else {
                    showNotification(
                        `${data.type} has been completed successfully`,
                        "success",
                        "Learn more",
                        navigate,
                        "/pages"
                    );
                }
            }
        };

        const handleTaskFailed = async (data) => {

            if (data?.error && data?.error === "Stopped manually") return

            if (AUTOMATIC_LOGIN_TASK_TYPES.has(data?.type)) return

            console.log("Task failed!!!:", data);

            if (
                data.project_id === selectedProject?.id ||
                data.projectId === selectedProject?.id
            ) {
                if (data.type === "UI Scan") {
                    showNotification(
                        `${data.type} could not be completed`,
                        "error",
                        "Learn more",
                        navigate,
                        "/tasks",
                        null,
                        `Page: ${data.pageTitle}. ${data?.error || ''}`
                    );
                } else {
                    showNotification(
                        `${data.type} could not be completed`,
                        "error",
                        "Learn more",
                        navigate,
                        "/tasks",
                        null,
                        data?.error || ''
                    );
                }
            }
        };

        // New: handle quotaExceeded
        const handleQuotaExceeded = (data) => {
            setQuotaInfo(data);
            setQuotaExceededOpen(true);
        };

        socketEventEmitter.on("taskCompleted", handleTaskCompleted);
        socketEventEmitter.on("taskFailed", handleTaskFailed);
        socketEventEmitter.on("quotaExceeded", handleQuotaExceeded);

        return () => {
            socketEventEmitter.off("taskCompleted", handleTaskCompleted);
            socketEventEmitter.off("taskFailed", handleTaskFailed);
            socketEventEmitter.off("quotaExceeded", handleQuotaExceeded);
        };
        // eslint-disable-next-line
    }, [
        id,
        allProjects.length,
        selectedProject?.id,
        showNotification,
        projectsToggler,
    ]);

    useEffect(() => {
        const fetchTaskLoadData = async () => {
            try {
                const res = await TaskService.getLoadStatus();
                setLoadStatus(res?.loadStatus);
            } catch (error) {
                console.error("Error fetching sites:", error);
            }
        };

        fetchTaskLoadData();

        const handleUpdateLoadStatus = async (data) => {
            setLoadStatus(data?.loadStatus);
        };

        const handleOpenEnterOTPModal = async () => {
            setEnterOTPOpen(true);
        };

        const handleSessionExpired = async (data) => {
            console.log("Session expired event received:", data);
            // Only show the modal if the expired session is for the currently selected project
            if (data.projectId === selectedProject?.id) {
                setSessionExpiredPayload(data || null);
                setSessionExpiredOpen(true);
            }
        };

        socketEventEmitter.on("taskLoadUpdate", handleUpdateLoadStatus);
        botSocketEventEmitter.on("request-code", handleOpenEnterOTPModal);
        botSocketEventEmitter.on("session-expired", handleSessionExpired);

        return () => {
            socketEventEmitter.off("taskLoadUpdate", handleUpdateLoadStatus);
            botSocketEventEmitter.off("request-code", handleOpenEnterOTPModal);
            botSocketEventEmitter.off("session-expired", handleSessionExpired);
        };
    }, [selectedProject?.id]);

    const toggleSidebar = () => {
        setCollapsedSidebar((prev) => !prev);
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleReviewModalOpen = () => {
        setReviewModalOpen(true);
    };

    const handleLogout = () => {
        handleMenuClose();
        if (!user.feedback) {
            handleReviewModalOpen();
        } else {
            logout();
            navigate("/login");
        }
    };

    const handleProfile = () => {
        handleMenuClose();
        navigate("/profile");
    };

    const handleProjectsClick = () => {
        navigate("/");
    }

    const handleProjectChange = (event) => {
        const selectedProjectId = event.target.value;
        const selectedProjectItem = allProjects.find(
            (project) => project.id === selectedProjectId
        );
        if (!selectedProjectItem) return;
        setSelectedProject(selectedProjectItem);
        sessionStorage.removeItem("projectSelectionCleared");
        if (location.pathname.startsWith("/project/")) {
            navigate(`/project/${selectedProjectItem.id}`);
        } else {
            navigate("/pages");
        }
    };

    return (
        <AppBar position="relative" className="header">
            <Box className="headerSection">
                <Box>
                    <IconButton onClick={toggleSidebar}>
                        {collapsedSidebar ? (
                            <MenuOutlinedIcon />
                        ) : (
                            <MenuOpenOutlinedIcon />
                        )}
                    </IconButton>
                </Box>
                <IconButton
                    onClick={handleProjectsClick}
                >
                    <HomeIcon />
                </IconButton>
                <ProjectSelect
                    allProjects={allProjects}
                    selectedProject={selectedProject}
                    handleProjectChange={handleProjectChange}
                />
            </Box>
            <Box className="headerSection">
                {loadStatus ? <TaskLoad loadStatus={loadStatus} /> : null}
                <IconButton
                    className="headerSectionProfileIcon"
                    onClick={handleMenuOpen}
                >
                    <AccountCircleIcon />
                </IconButton>
            </Box>
            <Menu
                className="headerSectionProfileMenu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <MenuItem className="headerSectionProfileMenuTitleItem">
                    <AccountCircleIcon className="headerSectionProfileMenuTitleItemIcon" />
                    <Box className="headerSectionProfileMenuTitleItemInfo">
                        <Typography>
                            {user?.full_name ? user.full_name : ""}
                        </Typography>
                        <Typography className="headerSectionProfileMenuTitleItemInfoEmail">
                            {user?.email ? user?.email : ""}
                        </Typography>
                    </Box>
                </MenuItem>
                <MenuItem
                    className="headerSectionProfileMenuItem"
                    onClick={handleProfile}
                >
                    <PersonIcon className="headerSectionProfileMenuItemIcon" />
                    Profile
                </MenuItem>
                <MenuItem
                    className="headerSectionProfileMenuItem"
                    onClick={handleLogout}
                >
                    <LogoutIcon className="headerSectionProfileMenuItemIcon" />
                    Sign out
                </MenuItem>
            </Menu>
            <ReviewModal
                reviewModalOpen={reviewModalOpen}
                setReviewModalOpen={setReviewModalOpen}
            />
            <SessionExpiredModal
                sessionExpiredOpen={sessionExpiredOpen}
                setSessionExpiredOpen={setSessionExpiredOpen}
                sessionExpiredPayload={sessionExpiredPayload}
                setSessionExpiredPayload={setSessionExpiredPayload}
            />
            <EnterOTPModal
                enterOTPOpen={enterOTPOpen}
                setEnterOTPOpen={setEnterOTPOpen}
                setLoginWasNotCompletedOpen={setLoginWasNotCompletedOpen}
            />
            <LoginWasNotCompletedModal
                loginWasNotCompletedOpen={loginWasNotCompletedOpen}
                setLoginWasNotCompletedOpen={setLoginWasNotCompletedOpen}
            />

            {/* New: Quota Exceeded Modal */}
            <Dialog
                open={quotaExceededOpen}
                onClose={() => setQuotaExceededOpen(false)}
            >
                <Box className="quotaExceededModal">
                    <DialogTitle className="quotaExceededModalTitle">
                        Daily LLM request limit reached
                    </DialogTitle>
                    <DialogContent className="quotaExceededModalContent">
                        {quotaInfo ? (
                            <>
                                <Typography variant="body1" sx={{ mb: 1.5 }}>
                                    You have used {quotaInfo.used} of{" "}
                                    {quotaInfo.limit} requests today.
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 1.5 }}>
                                    This action requires {quotaInfo.required}{" "}
                                    requests, but you have only{" "}
                                    {quotaInfo.remaining} remaining.
                                </Typography>
                            </>
                        ) : null}
                        <Typography variant="body2" color="text.secondary">
                            Please try again tomorrow or reduce the number of
                            requested operations or do not add login to your
                            project.
                        </Typography>
                    </DialogContent>
                    <DialogActions className="quotaExceededModalActions">
                        <Button
                            className="quotaExceededModalButton"
                            onClick={() => setQuotaExceededOpen(false)}
                            color="secondary"
                            variant="contained"
                            autoFocus
                        >
                            OK
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </AppBar>
    );
};

export default Header;
